import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost, ApiError } from "@/lib/api";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { RichText } from "@/components/content/RichText";
import type { PageDto } from "@/types";

/**
 * Public contact page.
 *
 * - Loads the editorial body of the `kontakt` page from `/public/pages?path=kontakt`
 *   and renders it above the contact form, so editors can keep the
 *   address / phone / opening hours up to date from the admin.
 * - Falls back to just the form when the page record is missing.
 */
export function ContactPage() {
  const { data: page } = useQuery<PageDto>({
    queryKey: ["page", "kontakt"],
    queryFn: () => apiGet<PageDto>("/public/pages/kontakt?locale=de"),
    // Don't surface a console error if the page record was deleted;
    // the page still works as a pure form.
    retry: false,
  });

  const [form, setForm] = useState({
    name: "", email: "", subject: "", message: "",
    consent: false, website: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");

  function update<K extends keyof typeof form>(k: K, v: typeof form[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    try {
      await apiPost("/public/contact", form);
      setStatus("ok");
      setForm({ name: "", email: "", subject: "", message: "", consent: false, website: "" });
    } catch (err) {
      if (err instanceof ApiError) setStatus("error");
      else setStatus("error");
    }
  }

  return (
    <>
      <Helmet><title>{"Kontakt"} | Falkenrehde</title></Helmet>
      <Breadcrumb items={[{ label: "Kontakt" }]} />
      <h1 className="font-serif text-3xl lg:text-4xl font-bold mb-6 text-primary">
        {page?.title ?? "Kontakt"}
      </h1>

      {page?.bodyHtml && (
        <div className="mb-8 max-w-2xl">
          <RichText html={page.bodyHtml} />
        </div>
      )}

      <h2 className="font-serif text-xl text-primary mb-3">Nachricht senden</h2>
      {status === "ok" ? (
        <div className="p-4 bg-success/10 border border-success/30 text-success rounded-md">
          {"Vielen Dank, Ihre Nachricht wurde übermittelt."}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 max-w-xl" noValidate>
          {/* Honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => update("website", e.target.value)}
            className="absolute -left-[9999px] w-0 h-0"
            aria-hidden="true"
          />
          <Field label={"Name"} value={form.name} onChange={(v) => update("name", v)} required />
          <Field label={"E-Mail"} value={form.email} onChange={(v) => update("email", v)} type="email" required />
          <Field label={"Betreff"} value={form.subject} onChange={(v) => update("subject", v)} required />
          <div>
            <label className="block text-sm font-medium mb-1">{"Nachricht"}</label>
            <textarea
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              minLength={10}
              maxLength={2000}
              required
              rows={6}
              className="w-full border border-border rounded-md px-3 py-2 focus:border-primary outline-none"
            />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => update("consent", e.target.checked)}
              required
              className="mt-1"
            />
            <span>{"Ich bin mit der Verarbeitung meiner Daten gemäß Datenschutzerklärung einverstanden."}</span>
          </label>
          {status === "error" && (
            <p className="text-error text-sm">{"Die Nachricht konnte nicht gesendet werden. Bitte später erneut versuchen."}</p>
          )}
          <button
            type="submit"
            disabled={status === "sending"}
            className="btn-primary disabled:opacity-60"
          >
            {"Absenden"}
          </button>
        </form>
      )}
    </>
  );
}

function Field({ label, value, onChange, type = "text", required }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full border border-border rounded-md px-3 py-2 focus:border-primary outline-none"
      />
    </div>
  );
}
